/* global gtag */

/* --- Load Plugins / Functions --- */
import { isDEVMODE, globalStorage, viewStorage, domStorage } from './_globals'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { GlobalIntro } from './animations/global-intro'

import viewScroll from './modules/view-scroll.js'
import viewPrllx from './modules/view-prllx.js'
import viewInView from './modules/view-inview.js'
import viewModals from './modules/view-modal'
import viewText from './modules/view-text'
import Bubbles from './modules/bubbles'
import MobileSliders from './modules/mobile-sliders'

const toggleHeaderTheme = () => {
  if (!domStorage.header) return

  if (viewStorage.current.dataset.header === 'light') {
    domStorage.header.classList.add('--light')
  } else {
    domStorage.header.classList.remove('--light')
  }
}

/**
 * Updates language links in navigation after a PJAX navigation
 * Uses JSON data embedded in the page loaded by Taxi.js
 * @param {Object} to - Taxi.js event object containing the loaded page information
 */
const updateLanguageLinks = (to) => {
  const langSwitcher = domStorage.menu?.querySelector('.Nav__lang-switcher')
  if (!langSwitcher) return

  try {
    let jsonDataElement = null

    // Try to access the HTML document loaded by Taxi.js
    // Method 1: If to.page is directly the document (as in the provided example)
    if (to.page && typeof to.page.getElementById === 'function') {
      jsonDataElement = to.page.getElementById('json_data')
    }
    // Method 2: Search in the current DOM (element has been injected into [data-taxi])
    if (!jsonDataElement) {
      jsonDataElement = document.getElementById('json_data')
    }
    // Method 3: Search in the renderer content
    if (!jsonDataElement && to.renderer && to.renderer.content) {
      jsonDataElement = to.renderer.content.querySelector('#json_data')
    }

    if (!jsonDataElement || !jsonDataElement.innerHTML) {
      if (isDEVMODE) console.warn('json_data element not found')
      return
    }

    const jsonData = JSON.parse(jsonDataElement.innerHTML)
    if (!jsonData.langs) {
      if (isDEVMODE) console.warn('No langs data in json_data')
      return
    }

    // Get all language links in the persistent navigation
    const langLinks = langSwitcher.querySelectorAll('.Nav__lang-link')

    // Update each link using hreflang to identify the language
    langLinks.forEach((link) => {
      const langCode = link.getAttribute('hreflang')
      if (langCode && jsonData.langs[langCode]) {
        const langData = jsonData.langs[langCode]

        // Update the URL
        if (langData.url) {
          link.setAttribute('href', langData.url)
        }

        // Update the active class
        if (langData.current_lang) {
          link.classList.add('--active')
          link.removeAttribute('data-router-disabled')
        } else {
          link.classList.remove('--active')
          link.setAttribute('data-router-disabled', '')
        }
      }
    })

    if (isDEVMODE) console.log('Language links updated')
  } catch (error) {
    if (isDEVMODE) console.error('Error updating language links:', error)
  }
}

/* --- DOMContentLoaded Function --- */
export const onReady = () => {

  const { body } = domStorage

  viewStorage.viewScroll = new viewScroll()
  viewStorage.viewPrllx = new viewPrllx(body)
  viewStorage.viewInView = new viewInView(body)
  viewStorage.viewModals = new viewModals(body)
  viewStorage.viewText = new viewText(body)
  viewStorage.Bubbles = new Bubbles()
  viewStorage.MobileSliders = new MobileSliders()

  setTimeout(() => window.scrollTo(globalStorage.pageScrollTop, globalStorage.pageScrollTop), 100)
  ScrollTrigger.refresh()

  // Intro
  GlobalIntro()
  toggleHeaderTheme()

}

export const onEnter = (to, trigger) => {

  // LoadingClasses
  const { body } = domStorage
  body.classList.remove('--loading')

  // ScrollTop
  window.scrollTo(globalStorage.pageScrollTop, globalStorage.pageScrollTop)

  // Update Current View
  viewStorage.current = to.renderer.content

  // Add Page Effects
  const { hasSmoothScroll } = viewStorage
  body.style.overflow = ''

  if (hasSmoothScroll) viewStorage.viewScroll.scroll.start()
  viewStorage.viewPrllx = new viewPrllx()
  viewStorage.viewInView = new viewInView()
  viewStorage.viewModals = new viewModals()
  viewStorage.viewText = new viewText(body)
  viewStorage.Bubbles = new Bubbles()
  viewStorage.MobileSliders = new MobileSliders()

}

export const onEnterCompleted = (to, from, trigger) => {

  if (isDEVMODE) console.log('onEnterCompleted')

  const { targetLocation } = globalStorage.taxi

  // Anchor
  if (targetLocation.hasHash) {
    const anchorEl = document.querySelector(window.location.hash)
    if (anchorEl) gsap.to(window, {duration: 1.8, scrollTo: { y: anchorEl, autoKill: false }, overwrite: 'all', ease: 'expo.inOut' })
  }

  // Analytics
  if (globalStorage.tarteaucitron && globalStorage.tarteaucitron.job.includes('gtag') && typeof gtag !== 'undefined') {
    gtag('config', globalStorage.tarteaucitron.user.gtagUa, {
      'page_path': targetLocation.pathname,
      'page_title': to.page.title,
      'page_location': targetLocation.href
    })
  }

  // WPML Update Language Links
  updateLanguageLinks(to)
}

export const onLeave = (from, trigger) => {

  // Remove Previous Page Effects
  const { hasInView, hasSticky, hasPrllx, hasModals, hasCursor, hasBubbles, hasMobileSliders } = viewStorage

  hasInView && viewStorage.viewInView.destroy()
  hasSticky && viewStorage.viewSticky.destroy()
  hasPrllx && viewStorage.viewPrllx.destroy()
  hasModals && viewStorage.viewModals.destroy()
  hasCursor && globalStorage.Cursor.removeHovers()
  hasBubbles && viewStorage.Bubbles.destroy()
  hasMobileSliders && viewStorage.MobileSliders.destroy()

  // LoadingClasses
  const { body } = domStorage
  body.classList.add('--loading')
  body.classList.add('--animating')

  const { menu } = domStorage
  const { menuOpen } = globalStorage

  // Close Menu
  menuOpen && globalStorage.closeMobileMenu()

  // Remove active links
  const { targetLocation } = globalStorage.taxi
  const navLinks = menu.querySelectorAll('.Nav__link')
  navLinks.forEach((link) => {
    if (link.href === targetLocation.raw) link.classList.add('--active')
    else link.classList.remove('--active')
  })

}
